import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { PropsWithChildren, useMemo } from 'react';
import TabGroup from '@sanlamcx/react-ui-next-tabs';

type InvestmentInformationTabsProps<T extends string> = {
  tabConfig: Record<T, { label: string; content: React.ReactNode }>;
  tabNames: T[];
  parentComponent?: string;
};

export function InvestmentInformationTabs<T extends string>({
  tabConfig,
  tabNames,
  parentComponent
}: PropsWithChildren<InvestmentInformationTabsProps<T>>) {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();
  const [searchParams] = useSearchParams();

  const normalizeTabParam = (param?: string | null): T => {
    if (!param) return (tabNames[0] ?? ('' as T)) as T;

    const cleaned = param.replace(/[^a-z0-9]/gi, '').toLowerCase();
    return (tabNames.find((t) => t.toLowerCase() === cleaned) ?? tabNames[0] ?? ('' as T)) as T;
  };

  const selectedTab = normalizeTabParam(searchParams.get('tab'));

  const updateRoute = (updatedTab: string) => {
    updatedTab = updatedTab.toLocaleLowerCase();

    if (parentComponent === 'InvestmentAndClientInfo') {
      if (!clientId) {
        return;
      }
      navigate(`/clients/${clientId}/client-profile?tab=${updatedTab}`, { replace: true });
    } else {
      navigate(`/investments?tab=${updatedTab}`, {
        replace: true
      });
    }
  };

  const tabs = useMemo(
    () =>
      tabNames.map((name) => ({
        name,
        label: tabConfig[name]?.label,
        content: tabConfig[name]?.content,
        showOverflow: true
      })),
    [tabConfig, tabNames]
  );

  return <TabGroup defaultSelected={selectedTab} onChange={updateRoute} tabs={tabs} />;
}
